import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  ChevronLeft, FileText, UserCheck, CalendarCheck, Car, Wrench,
  AlertTriangle, Clock, CreditCard, ShieldAlert, Gavel, HelpCircle,
  CheckCircle2, ArrowRight
} from 'lucide-react';
import HireHubLogo from '../components/HireHubLogo';
import { usePaymentMode } from '../contexts/PaymentModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, title, color, children }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-6 sm:p-8" data-testid={`section-${title.toLowerCase().replace(/[\s&]+/g, '-')}`}>
    <div className="flex items-start gap-4 mb-4">
      <div className={`shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-slate-900 pt-1.5">{title}</h2>
    </div>
    <div className="text-sm sm:text-base text-slate-600 leading-relaxed space-y-3 pl-0 sm:pl-[60px]">
      {children}
    </div>
  </div>
);

const Check = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

const Warn = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

export default function RenterAgreementPage() {
  const [meta, setMeta] = useState(null);
  const { isHostDirect } = usePaymentMode();

  useEffect(() => {
    axios.get(`${API_URL}/api/legal/renter-agreement`)
      .then(r => setMeta(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Renter Agreement | HireHub Australia</title>
        <meta name="description" content="Understand your responsibilities when booking a vehicle through HireHub. Learn about vehicle use, damage reporting, payments, and platform rules." />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/">
              <HireHubLogo size="md" />
            </Link>
          </div>
          <Link to="/vehicles">
            <Button variant="outline" size="sm" className="h-9 text-sm">
              Browse Vehicles
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#004B8D] to-[#003366] text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <FileText className="w-4 h-4" />
            <span className="text-sm font-medium">Renter Agreement</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="renter-agreement-hero-title">
            Renter Agreement
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            Your responsibilities when booking through HireHub.
          </p>
          <p className="text-sm sm:text-base text-white/60 max-w-xl mx-auto mt-4 leading-relaxed">
            By booking a vehicle on HireHub, you agree to follow these rules. They exist to keep things safe, fair, and straightforward for everyone.
          </p>
          <p className="text-xs text-white/40 max-w-lg mx-auto mt-5 leading-relaxed italic">
            This agreement forms part of HireHub's platform terms and may be referenced in disputes.
          </p>
          {meta?.published_at && (
            <p className="text-xs text-white/40 mt-3 flex items-center justify-center gap-1.5" data-testid="last-updated">
              <Clock className="w-3 h-3" />
              Last updated: {new Date(meta.published_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
              {meta.version_id && <span className="ml-1">({meta.version_id})</span>}
            </p>
          )}
        </div>
      </section>

      {/* Sections */}
      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 space-y-5">

          {/* 1. Eligibility & Verification */}
          <Section icon={UserCheck} title="Eligibility & Verification" color="bg-blue-600">
            <p>Before you can book, you need to confirm who you are. This protects both you and the vehicle host.</p>
            <div className="space-y-1.5 pt-1">
              <Check>Provide accurate identity details that match your licence</Check>
              <Check>Complete verification when prompted (licence upload + selfie)</Check>
              <Check>Do not use another person's licence or identity documents</Check>
              <Check>Ensure your licence is valid and not expired</Check>
            </div>
          </Section>

          {/* 2. Booking Responsibilities */}
          <Section icon={CalendarCheck} title="Booking Responsibilities" color="bg-emerald-600">
            <p>Once you make a booking request, you're committing to the agreed details. Clear communication avoids problems.</p>
            <div className="space-y-1.5 pt-1">
              <Check>Only book vehicles you genuinely intend to use</Check>
              <Check>Provide accurate trip dates, times, and pickup details</Check>
              <Check>Communicate honestly and promptly with the host</Check>
              <Check>Review the vehicle listing and host rules before confirming</Check>
            </div>
          </Section>

          {/* 3. Vehicle Use Rules */}
          <Section icon={Car} title="Vehicle Use Rules" color="bg-violet-600">
            <p>When you're behind the wheel of someone else's vehicle, clear boundaries apply.</p>
            <div className="space-y-1.5 pt-1">
              <Check>Use the vehicle only for the agreed purpose</Check>
              <Check>No illegal activity of any kind</Check>
              <Check>No unauthorised drivers — only the verified renter may drive</Check>
              <Check>Follow all applicable road laws, speed limits, and traffic regulations</Check>
              <Check>Do not take the vehicle outside any agreed geographic area</Check>
            </div>
          </Section>

          {/* 4. Care of Vehicle */}
          <Section icon={Wrench} title="Care of Vehicle" color="bg-teal-600">
            <p>Treat the vehicle as if it were your own. Return it in the same condition you received it.</p>
            <div className="space-y-1.5 pt-1">
              <Check>Return the vehicle in the same condition it was collected</Check>
              <Check>Keep the interior clean and free of rubbish</Check>
              <Check>Refuel as agreed with the host (typically return at same fuel level)</Check>
              <Check>Report any mechanical issues, warning lights, or concerns immediately</Check>
            </div>
          </Section>

          {/* 5. Damage & Incidents */}
          <Section icon={AlertTriangle} title="Damage & Incidents" color="bg-red-600">
            <p>If something goes wrong, honesty and speed are what matter most.</p>
            <div className="space-y-1.5 pt-1">
              <Check>Report any damage immediately — no matter how minor</Check>
              <Check>Provide honest and accurate information about what happened</Check>
              <Check>Cooperate fully with the claims and dispute process</Check>
              <Check>Do not attempt to hide, minimise, or delay reporting damage</Check>
            </div>
            <p className="text-slate-500 text-sm pt-1">
              Delayed or dishonest reporting may result in additional charges and account action.
            </p>
          </Section>

          {/* 6. Timing & Returns */}
          <Section icon={Clock} title="Timing & Returns" color="bg-amber-600">
            <p>Returning on time is a basic courtesy — and a firm expectation.</p>
            <div className="space-y-1.5 pt-1">
              <Check>Return the vehicle at the agreed time and location</Check>
              <Check>If you're running late, notify the host as early as possible</Check>
              <Check>Follow the agreed pickup and return process (inspections, keys, location)</Check>
            </div>
            <div className="pt-1">
              <Warn>Late returns may incur additional daily or hourly charges</Warn>
            </div>
          </Section>

          {/* 7. Payments & Charges */}
          <Section icon={CreditCard} title="Payments & Charges" color="bg-indigo-600">
            <p>All costs are transparent and agreed upfront. Additional charges only apply in specific situations.</p>
            <div className="space-y-1.5 pt-1">
              <Check>Agree to the full booking cost before confirming the hire</Check>
              {isHostDirect ? (
                <Check>Arrange payment directly with the host using the agreed method (PayID, bank transfer, or another mutually agreed method). HireHub does not process payments in this mode.</Check>
              ) : (
                <Check>Pay through the HireHub platform only — no off-platform payments</Check>
              )}
              <Check>A security bond may be authorised before the hire begins</Check>
            </div>
            <p className="text-slate-500 text-sm pt-2">Additional charges may apply for:</p>
            <div className="grid grid-cols-2 gap-1.5 pt-1">
              <Warn>Damage to the vehicle</Warn>
              <Warn>Missing or low fuel</Warn>
              <Warn>Unpaid tolls or fines</Warn>
              <Warn>Late return fees</Warn>
            </div>
          </Section>

          {/* 8. Platform Rules */}
          <Section icon={ShieldAlert} title="Platform Rules" color="bg-slate-700">
            {isHostDirect ? (
              <p>HireHub keeps the booking workflow, evidence, and communication on-platform so every action is recorded, even though payment is arranged directly between host and renter.</p>
            ) : (
              <p>HireHub exists to keep transactions safe and fair. Working outside the platform puts everyone at risk.</p>
            )}
            <div className="space-y-1.5 pt-1">
              {isHostDirect ? (
                <Check>Keep the booking workflow (confirmation, checklists, evidence, disputes) on HireHub — even though payment is arranged directly with the host</Check>
              ) : (
                <Check>Do not arrange off-platform deals with hosts</Check>
              )}
              <Check>Do not share personal contact details to bypass HireHub</Check>
              <Check>Respect the platform's communication and dispute processes</Check>
              <Check>Do not create duplicate or fraudulent accounts</Check>
            </div>
          </Section>

          {/* 9. Enforcement */}
          <Section icon={Gavel} title="Enforcement" color="bg-rose-700">
            <p>We enforce these rules to protect the community. Violations are taken seriously.</p>
            <p>Breaching this agreement may result in:</p>
            <div className="space-y-1.5 pt-1">
              <Warn>Temporary or permanent account restriction</Warn>
              <Warn>Additional charges applied to your booking</Warn>
              <Warn>Forfeiture of security bond</Warn>
              <Warn>Removal from the HireHub platform</Warn>
            </div>
            <p className="text-slate-500 text-sm pt-1">
              All enforcement decisions are evidence-based and subject to admin review.
            </p>
          </Section>

        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-14 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <HelpCircle className="w-10 h-10 text-[#004B8D] mx-auto mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Have questions?</h2>
          <p className="text-slate-600 text-sm sm:text-base mb-6">
            Visit our Contact page or check out our Trust & Safety overview for more details.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/contact">
              <Button className="bg-[#004B8D] hover:bg-[#003a6d] text-white px-6" data-testid="cta-contact">
                Contact Support <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/trust-safety">
              <Button variant="outline" className="px-6" data-testid="cta-trust-safety">
                Trust & Safety
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900 text-slate-400 text-center text-xs sm:text-sm">
        <p>&copy; {new Date().getFullYear()} HireHub Australia. All rights reserved.</p>
        <div className="flex items-center justify-center gap-4 mt-3 text-slate-500">
          <Link to="/legal/terms" className="hover:text-white transition-colors">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/trust-safety" className="hover:text-white transition-colors">Trust & Safety</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        </div>
      </footer>
    </div>
  );
}
